import type { Website } from "@/constants/features";
import * as api from "../client";
import type {
  BusinessType,
  ContentStatus,
  FirmProfile,
  ListResponse,
  ProfileType,
  RegionAssignmentInput,
  StatusUpdatePayload,
} from "./types";

const BASE = "/v1/profiles/firm-profiles";

export type FirmProfileFilters = {
  companyId?: string;
  /**
   * profiles-api expects `profileType` (lawfirm/set/network). The list page
   * exposes this as a `businessType` filter — we accept either name here for
   * convenience.
   */
  profileType?: ProfileType;
  businessType?: BusinessType;
  status?: string;
  website?: Website;
  q?: string;
  /**
   * Region filter (§3.4): `regionKind` + `regionId` (integer) must be supplied
   * together or not at all. `regionId` is the integer `api_region_country_v2`
   * id; `regionKind` discriminates the region- vs country-id space.
   */
  regionKind?: "region" | "country";
  regionId?: string;
  limit?: number;
  offset?: number;
};

export type FirmProfileCreateInput = {
  companyId?: string;
  /** Content-addressed logo image reference (mirrors lawyer photo). */
  logoHash?: string | null;
  logoFilename?: string | null;
  languageCode?: string;
  /** Publishing target / edition (DEV spec 319). */
  website?: Website;
  profileType: ProfileType;
  theFirm?: string;
  additionalInformation?: string;
  status: ContentStatus;
  staffing?: Array<{ title: string; value: string }>;
  memberships?: Array<{ membershipText: string }>;
  languages?: Array<{ languageCode: string }>;
  /** At least one region required server-side; UI also enforces this. */
  regions?: RegionAssignmentInput[];
};

export type FirmProfileUpdateInput = Partial<FirmProfileCreateInput>;

export const firmProfilesService = {
  async list(filters: FirmProfileFilters = {}): Promise<ListResponse<FirmProfile>> {
    const params = new URLSearchParams();
    if (filters.companyId) params.append("companyId", filters.companyId);
    const profileType = filters.profileType ?? filters.businessType;
    if (profileType) params.append("profileType", profileType);
    if (filters.status) params.append("status", filters.status);
    if (filters.website) params.append("website", filters.website);
    if (filters.q) params.append("search", filters.q);
    if (filters.regionKind && filters.regionId) {
      params.append("regionKind", filters.regionKind);
      params.append("regionId", filters.regionId);
    }
    if (filters.limit !== undefined) params.append("limit", String(filters.limit));
    if (filters.offset !== undefined) params.append("offset", String(filters.offset));
    const query = params.toString() ? `?${params}` : "";
    const raw = await api.get<{
      firmProfiles: FirmProfile[];
      pagination: { total: number; limit: number; offset: number };
    }>(`${BASE}${query}`);
    return {
      data: raw.firmProfiles ?? [],
      total: raw.pagination?.total ?? 0,
      limit: raw.pagination?.limit ?? 20,
      offset: raw.pagination?.offset ?? 0,
    };
  },

  async get(id: string): Promise<FirmProfile> {
    const raw = await api.get<{ firmProfile: FirmProfile } | FirmProfile>(
      `${BASE}/${encodeURIComponent(id)}`,
    );
    return (raw as { firmProfile?: FirmProfile }).firmProfile ?? (raw as FirmProfile);
  },

  create(input: FirmProfileCreateInput): Promise<FirmProfile> {
    return api.post<FirmProfile>(`${BASE}`, input);
  },

  update(id: string, input: FirmProfileUpdateInput): Promise<FirmProfile> {
    return api.put<FirmProfile>(`${BASE}/${encodeURIComponent(id)}`, input);
  },

  remove(id: string): Promise<void> {
    return api.del<void>(`${BASE}/${encodeURIComponent(id)}`);
  },

  async updateStatus(id: string, payload: StatusUpdatePayload): Promise<FirmProfile> {
    const raw = await api.patch<{ firmProfile: FirmProfile } | FirmProfile>(
      `${BASE}/${encodeURIComponent(id)}/status`,
      payload,
    );
    return (raw as { firmProfile?: FirmProfile }).firmProfile ?? (raw as FirmProfile);
  },
};
